import { useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { format } from "date-fns";
import { da } from "date-fns/locale";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { PlayCircle, CheckCircle, XCircle, MessageSquare, ChevronDown, ChevronUp } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";
import { TaskComment } from "@/types/admin";
import { TaskImages } from "@/components/task-images/TaskImages";

interface TaskScheduleListProps {
  schedules: any[];
  isLoading: boolean;
  selectedDate: Date;
}

export const TaskScheduleList = ({ schedules, isLoading, selectedDate }: TaskScheduleListProps) => {
  const { toast } = useToast();
  const [expandedTask, setExpandedTask] = useState<string | null>(null);
  const [newComment, setNewComment] = useState<string>('');
  const [comments, setComments] = useState<Record<string, TaskComment[]>>({});
  const [loadingComments, setLoadingComments] = useState<Record<string, boolean>>({});

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-500">Afsluttet</Badge>;
      case 'in-progress':
        return <Badge className="bg-blue-500">I gang</Badge>;
      case 'pending':
      default:
        return <Badge variant="secondary">Ikke påbegyndt</Badge>;
    }
  };

  const updateTaskStatus = async (taskId: string, newStatus: 'pending' | 'in-progress' | 'completed') => {
    try {
      const { error } = await supabase
        .from('tasks')
        .update({ status: newStatus })
        .eq('id', taskId);

      if (error) throw error;

      toast({
        title: "Status opdateret",
        description: "Opgavens status er blevet opdateret",
      });
    } catch (error) {
      console.error('Fejl ved opdatering af status:', error);
      toast({
        title: "Fejl ved opdatering",
        description: "Der skete en fejl ved opdatering af status",
        variant: "destructive",
      });
    }
  };

  const fetchComments = async (taskId: string) => {
    setLoadingComments(prev => ({ ...prev, [taskId]: true }));
    try {
      const { data, error } = await supabase
        .from('task_comments')
        .select('*, profiles!task_comments_user_id_fkey(first_name, last_name)')
        .eq('task_id', taskId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (data) {
        const formattedComments: TaskComment[] = data.map(comment => ({
          ...comment,
          profiles: comment.profiles as { first_name: string | null; last_name: string | null; } | null
        }));
        setComments(prev => ({ ...prev, [taskId]: formattedComments }));
      }
    } catch (error) {
      console.error('Fejl ved hentning af kommentarer:', error);
      toast({
        title: "Fejl ved hentning af kommentarer",
        description: "Der skete en fejl. Prøv igen senere.",
        variant: "destructive",
      });
    } finally {
      setLoadingComments(prev => ({ ...prev, [taskId]: false }));
    }
  };

  const handleExpandTask = async (taskId: string) => {
    if (expandedTask === taskId) {
      setExpandedTask(null);
    } else {
      setExpandedTask(taskId);
      if (!comments[taskId]) {
        await fetchComments(taskId);
      }
    }
  };

  const addComment = async (taskId: string) => {
    if (!newComment.trim()) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Ikke logget ind');

      const { error } = await supabase
        .from('task_comments')
        .insert({
          task_id: taskId,
          user_id: session.user.id,
          comment: newComment.trim()
        });

      if (error) throw error;

      setNewComment('');
      await fetchComments(taskId);
      
      toast({
        title: "Kommentar tilføjet",
        description: "Din kommentar er blevet tilføjet til opgaven",
      });
    } catch (error) {
      console.error('Fejl ved tilføjelse af kommentar:', error);
      toast({
        title: "Fejl ved tilføjelse af kommentar",
        description: "Der skete en fejl. Prøv igen senere.",
        variant: "destructive",
      });
    }
  };

  const TaskCard = ({ schedule, status }: { schedule: any, status: string }) => (
    <div className="p-4 rounded-lg border bg-white shadow-sm">
      <div className="flex justify-between items-start mb-2">
        <h4 className="font-medium">{schedule.tasks?.title}</h4>
        {getStatusBadge(status)}
      </div>
      
      <p className="text-sm text-muted-foreground mb-2">
        {schedule.profiles?.first_name} {schedule.profiles?.last_name}
      </p>
      
      <div className="text-sm text-muted-foreground mb-3">
        {format(new Date(schedule.start_time), "HH:mm")} -{" "}
        {format(new Date(schedule.end_time), "HH:mm")}
      </div>

      {schedule.tasks?.task_description && (
        <div className="mb-3 text-sm bg-gray-50 p-2 rounded">
          {schedule.tasks.task_description}
        </div>
      )}

      {status === 'pending' && (
        <Button 
          size="sm" 
          className="w-full mb-2"
          onClick={() => updateTaskStatus(schedule.tasks?.id, 'in-progress')}
        >
          <PlayCircle className="w-4 h-4 mr-2" />
          Start opgave
        </Button>
      )}

      {status === 'in-progress' && (
        <div className="grid grid-cols-2 gap-2 mb-2">
          <Button 
            size="sm" 
            variant="outline"
            onClick={() => updateTaskStatus(schedule.tasks?.id, 'pending')}
          >
            <XCircle className="w-4 h-4 mr-2" />
            Stop
          </Button>
          <Button 
            size="sm"
            onClick={() => updateTaskStatus(schedule.tasks?.id, 'completed')}
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            Afslut
          </Button>
        </div>
      )}

      <Button
        variant="ghost"
        size="sm"
        className="w-full flex items-center justify-center"
        onClick={() => handleExpandTask(schedule.tasks?.id)}
      >
        <MessageSquare className="w-4 h-4 mr-2" />
        Kommentarer
        {expandedTask === schedule.tasks?.id ? (
          <ChevronUp className="w-4 h-4 ml-2" />
        ) : (
          <ChevronDown className="w-4 h-4 ml-2" />
        )}
      </Button>

      {expandedTask === schedule.tasks?.id && (
        <div className="mt-4 space-y-4">
          <div className="space-y-4">
            <TaskImages taskId={schedule.tasks?.id} />
            <Textarea
              placeholder="Skriv en kommentar..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              className="bg-white"
            />
            <Button 
              size="sm"
              onClick={() => addComment(schedule.tasks?.id)}
              className="w-full"
            >
              Tilføj kommentar
            </Button>
          </div>

          {loadingComments[schedule.tasks?.id] ? (
            <div className="text-center">Indlæser kommentarer...</div>
          ) : (
            <div className="space-y-2">
              {comments[schedule.tasks?.id]?.map((comment) => (
                <div key={comment.id} className="bg-gray-50 p-2 rounded text-sm">
                  <div className="font-medium">
                    {comment.profiles?.first_name} {comment.profiles?.last_name}
                  </div>
                  <div className="text-gray-600">
                    {format(new Date(comment.created_at), "d. MMM yyyy HH:mm", { locale: da })}
                  </div>
                  <div className="mt-1">{comment.comment}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  if (schedules.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Ingen opgaver planlagt for {format(selectedDate, "d. MMMM yyyy", { locale: da })}
      </div>
    );
  }

  const groupedSchedules = {
    pending: schedules.filter(s => s.tasks?.status === 'pending'),
    inProgress: schedules.filter(s => s.tasks?.status === 'in-progress'),
    completed: schedules.filter(s => s.tasks?.status === 'completed')
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="space-y-4">
        <h3 className="font-medium text-lg text-center pb-2 border-b">
          Ikke påbegyndt ({groupedSchedules.pending.length})
        </h3>
        {groupedSchedules.pending.map((schedule) => (
          <TaskCard key={schedule.id} schedule={schedule} status="pending" />
        ))}
      </div>

      <div className="space-y-4">
        <h3 className="font-medium text-lg text-center pb-2 border-b">
          I gang ({groupedSchedules.inProgress.length})
        </h3>
        {groupedSchedules.inProgress.map((schedule) => (
          <TaskCard key={schedule.id} schedule={schedule} status="in-progress" />
        ))}
      </div>

      <div className="space-y-4">
        <h3 className="font-medium text-lg text-center pb-2 border-b">
          Afsluttet ({groupedSchedules.completed.length})
        </h3>
        {groupedSchedules.completed.map((schedule) => (
          <TaskCard key={schedule.id} schedule={schedule} status="completed" />
        ))}
      </div>
    </div>
  );
};
